import { FC, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import { Check, Info, Settings } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  Heading,
  Icon,
  RadiusToken,
  SpacingToken,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { isInIframe } from "helpers/app";
import { openInNewCard } from "helpers/routing";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { useMacros } from "hooks/use-macros";
import { ITicket } from "interfaces/entities/ticket";
import { AutomationsRoutes } from "routes/automations/constants";
import { selectLoggedInAgentPermission } from "store/entities/agents";
import { IMacro } from "store/entities/macros/interfaces";
import { TicketActions } from "store/entities/tickets/actions";
import { textEllipsisStyles } from "styles/common";
import { withVar } from "themes/helpers";

import { filterMacrosAlphabeticallyInTicket } from "../helpers";

import { MacroPreview } from "./MacroPreview";

interface IMacroPickerItemProps {
  macro: IMacro;
  isSelected: boolean;
  hidePreview?: boolean;
  onSelect: () => void;
}

const MacroPickerItem: FC<IMacroPickerItemProps> = ({ macro, isSelected = false, hidePreview, onSelect }) => {
  const { name } = macro;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleMacroClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect();
  };

  return (
    <Tooltip
      isVisible={hidePreview ? false : undefined}
      hoverOnDelay={400}
      placement="right-start"
      triggerRenderer={() => (
        <Flex
          alignItems="center"
          justifyContent="space-between"
          minHeight="40px"
          onClick={handleMacroClick}
          marginH={withVar(SpacingToken.Spacing2)}
          paddingH={withVar(SpacingToken.Spacing2)}
          borderRadius={withVar(RadiusToken.Radius3)}
          bg={withVar(isSelected ? DesignToken.PickerListOptionBackgroundActive : DesignToken.PickerListBackground)}
          css={{
            cursor: "pointer",
            "&:hover": {
              background: withVar(
                isSelected
                  ? DesignToken.PickerListOptionBackgroundActiveHover
                  : DesignToken.PickerListOptionBackgroundHover
              ),
            },
          }}
        >
          <Text css={{ color: withVar(DesignToken.ContentBasicPrimary), ...textEllipsisStyles }}>{name}</Text>
          {isSelected && (
            <Icon
              source={Check}
              customColor={withVar(DesignToken.ContentBasicInfo)}
              data-testid="selected-macro-check-icon"
            />
          )}
        </Flex>
      )}
    >
      <MacroPreview macro={macro} />
    </Tooltip>
  );
};

interface IMacroPickerItemHeaderProps {
  headerName: string;
}

const MacroPickerItemHeader: FC<IMacroPickerItemHeaderProps> = ({ headerName }) => (
  <Block
    padding={`${withVar(SpacingToken.Spacing3)} ${withVar(SpacingToken.Spacing4)} ${withVar(SpacingToken.Spacing1)}`}
    bg={withVar(DesignToken.PickerListGroupBackground)}
  >
    <Text caps bold css={{ color: withVar(DesignToken.ContentBasicSecondary) }}>
      {headerName}
    </Text>
  </Block>
);

interface IProps extends IWithBreakpointsProps {
  ticket: ITicket;
  onClose: () => void;
}

const MacroPickerComponent: FC<IProps> = ({ onClose, ticket, isMobile }) => {
  const userRole = useSelector(selectLoggedInAgentPermission);
  const history = useHistory();
  const dispatch = useDispatch();
  const { privateMacros, sharedMacros, macros } = useMacros();
  const [selectedMacro, setSelectedMacro] = useState<IMacro | null>(null);
  const {
    assignment: {
      team: { id: ticketAssignmentTeamId },
    },
  } = ticket;
  const inIframe = isInIframe();

  const filteredPrivateMacros = useMemo(
    () => filterMacrosAlphabeticallyInTicket(privateMacros, ticketAssignmentTeamId),
    [privateMacros, ticketAssignmentTeamId]
  );

  const filteredSharedMacros = useMemo(
    () => filterMacrosAlphabeticallyInTicket(sharedMacros, ticketAssignmentTeamId),
    [sharedMacros, ticketAssignmentTeamId]
  );

  const shouldDisplayEmptyState =
    (macros && !macros.length) || (!filteredPrivateMacros.length && !filteredSharedMacros.length);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleApplyMacro = () => {
    if (!selectedMacro) {
      return;
    }
    dispatch(
      TicketActions.applyMacro({ ticketId: ticket.id, macroId: selectedMacro.id, actions: selectedMacro.actions })
    );
    trackEngagement("Ticket details - Macros: Apply macro clicked", EventPlace.Automations, {
      role: userRole,
      macroType: selectedMacro.agentId ? "private" : "shared",
    });
    onClose();
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleOptionsClick = () => {
    if (inIframe) {
      openInNewCard(AutomationsRoutes.Macros);
    } else {
      history.push(AutomationsRoutes.Macros);
    }
    trackEngagement("Ticket details - Macros: Go to settings clicked", EventPlace.Automations, {
      role: userRole,
    });
    onClose();
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleCreateMacro = () => {
    if (inIframe) {
      openInNewCard(AutomationsRoutes.AddMacro);
    } else {
      history.push(AutomationsRoutes.AddMacro);
    }
    trackEngagement("Ticket details - Macros: Empty state create macro clicked", EventPlace.Automations, {
      role: userRole,
    });
    onClose();
  };

  if (shouldDisplayEmptyState) {
    return (
      <Flex
        width="320px"
        gap="8px"
        column
        paddingV={withVar(SpacingToken.Spacing12)}
        paddingH={withVar(SpacingToken.Spacing6)}
        alignItems="center"
        css={{ boxSizing: "border-box" }}
      >
        <Icon source={Info} size="xxlarge" />
        <Heading size="sm" css={{ color: withVar(DesignToken.ContentBasicPrimary) }}>
          No macros to apply
        </Heading>
        <Text css={{ color: withVar(DesignToken.ContentBasicPrimary), textAlign: "center" }}>
          {isMobile
            ? "Open HelpDesk on the desktop and create a macro to trigger a pre-set series of actions in the ticket you're currently handling."
            : "Create a macro to trigger a pre-set series of actions in the ticket you're currently handling."}
        </Text>
        {!isMobile && (
          <Button kind="link" onClick={handleCreateMacro}>
            Create macro
          </Button>
        )}
      </Flex>
    );
  }

  return (
    <Flex width="320px" column>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        padding={withVar(SpacingToken.Spacing3)}
        css={{ boxSizing: "border-box" }}
      >
        <Heading>Macros</Heading>
        {!isMobile && <Button kind="plain" icon={<Settings />} onClick={handleOptionsClick} tabIndex={-1} />}
      </Flex>
      <Flex width="100%" column maxHeight="320px" gap={withVar(SpacingToken.Spacing05)} overflowY="scroll">
        {filteredPrivateMacros.length > 0 && <MacroPickerItemHeader headerName="Private" />}
        {filteredPrivateMacros.map(macro => (
          <MacroPickerItem
            key={macro.id}
            macro={macro}
            isSelected={selectedMacro?.id === macro.id}
            onSelect={() => setSelectedMacro(macro)}
            hidePreview={isMobile}
          />
        ))}
        {filteredSharedMacros.length > 0 && <MacroPickerItemHeader headerName="Shared" />}
        {filteredSharedMacros.map(macro => (
          <MacroPickerItem
            key={macro.id}
            macro={macro}
            isSelected={selectedMacro?.id === macro.id}
            onSelect={() => setSelectedMacro(macro)}
            hidePreview={isMobile}
          />
        ))}
      </Flex>
      <Block padding={withVar(SpacingToken.Spacing3)}>
        <Button kind="primary" fullWidth onClick={handleApplyMacro} disabled={!selectedMacro}>
          Apply macro
        </Button>
      </Block>
    </Flex>
  );
};

export const MacroPicker = withBreakpoints(MacroPickerComponent);
